import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Toolbar,
  IconButton,
  Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate, useParams } from 'react-router';
import { ArrowBack } from '@mui/icons-material';
import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import { useApi } from '@/providers/ApiContext';
import { getTask } from '@/actions/task';
import toNiceTimestamp from '@/util/toNiceTimestamp';
import type { Task } from '@/types';

function AccQsureAdminTask(): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const { task_id } = useParams<{ task_id: string }>();
  const [task, setTask] = useState<Task | null>(null);
  const [loading_task, setLoadingTask] = useState<boolean>(true);

  useEffect(() => {
    if (user?.entity_id && !task && task_id && api) {
      getTask(api, task_id)
        .then((data) => {
          setTask(data);
          setLoadingTask(false);
        })
        .catch((error) => {
          console.log(error);
          setLoadingTask(false);
        });
    }
  }, [api, user?.entity_id, task, task_id]);

  const renderJsonField = (
    data: Record<string, unknown> | null | undefined,
  ): React.JSX.Element => {
    if (!data) return <span>N/A</span>;
    return (
      <Paper
        variant='outlined'
        sx={{
          p: 1,
          backgroundColor: '#f5f5f5',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          maxHeight: '200px',
          overflow: 'auto',
        }}
      >
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </Paper>
    );
  };

  const renderTimestamp = (timestamp: string | null | undefined): string => {
    if (!timestamp) return 'N/A';

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return toNiceTimestamp(timestamp, true);
    } catch (error) {
      console.warn('Error formatting timestamp:', timestamp, error);
      return 'Invalid Date';
    }
  };

  if (loading_task) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {task?.name}
        </Typography>
      </Toolbar>
      <Divider />

      <Box padding='15px'>
        {/* Basic Information Section */}
        <Typography variant='h6' sx={{ mb: 2 }}>
          Basic Information
        </Typography>
        <Divider />
        <br />
        <Grid container component='dl' spacing={0} sx={{ display: 'table' }}>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Entity ID:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {task?.entity_id}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Name:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {task?.name}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Type:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {task?.type}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Status:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {task?.status}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Created:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {renderTimestamp(task?.created_at)}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Updated:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {renderTimestamp(task?.updated_at)}
            </Typography>
          </Grid>
          <Grid>
            <Typography
              component='dt'
              variant='body1'
              sx={{ display: 'table-cell', width: '250px' }}
            >
              <b>Expires:</b>
            </Typography>
            <Typography
              component='dd'
              variant='body2'
              sx={{ display: 'table-cell' }}
            >
              {task?.expires_at ? renderTimestamp(task.expires_at) : 'N/A'}
            </Typography>
          </Grid>
        </Grid>

        {/* Metadata Section */}
        <Typography variant='h6' sx={{ mt: 4, mb: 2 }}>
          Metadata
        </Typography>
        <Divider />
        <br />
        <Box sx={{ mb: 2 }}>{renderJsonField(task?.metadata)}</Box>

        {/* Payload Section */}
        <Typography variant='h6' sx={{ mt: 4, mb: 2 }}>
          Payload
        </Typography>
        <Divider />
        <br />
        <Box sx={{ mb: 2 }}>{renderJsonField(task?.payload)}</Box>

        {/* Response Section */}
        <Typography variant='h6' sx={{ mt: 4, mb: 2 }}>
          Response
        </Typography>
        <Divider />
        <br />
        <Box sx={{ mb: 2 }}>{renderJsonField(task?.response)}</Box>
      </Box>
    </>
  );
}

export default AccQsureAdminTask;
